import type {ResumenDeInventario} from '@cnc/contracts';
import {Link} from 'react-router';
import {useResumenDeInventario} from '../api/inventario';
import {ComparativoAnual} from '../componentes/comparativo-anual';
import {FrescuraDeDatos} from '../componentes/frescura-de-datos';
import {AYUDA_INICIO as A} from '../componentes/ayuda-general';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PendientesHoy} from '../componentes/pendientes-hoy';
import {useVe} from '../componentes/sesion-contexto';
import {localeActual, tr, type Texto} from '../i18n/idioma';
import {numero} from '../utilidades/formato';

// Tablero de inicio: los pendientes de hoy, el comparativo, lo que hay en
// bodega, la distribucion por exportador, el historico y la ultima carga de
// cada archivo. Todo sale de la base de datos.
export function PaginaInicio() {
  // El tablero y el comparativo son del Consolidado (decision 0062): quien no
  // lo ve tiene solo sus pendientes.
  const veConsolidado = useVe('inventario.consolidado');
  const consulta = useResumenDeInventario(veConsolidado);
  if (!veConsolidado)
    return (
      <section className="max-w-6xl space-y-6">
        <h1 className="text-2xl font-semibold text-verde-oscuro">{tr('Inicio', 'Home')}</h1>
        <PendientesHoy />
        <FrescuraDeDatos />
      </section>
    );

  return (
    <section className="max-w-6xl">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-verde-oscuro">
          {tr('Inventario y exportaciones', 'Inventory and exports')}
        </h1>
        {consulta.data && (
          <span className="text-xs text-apagado">
            {tr('Actualizado', 'Updated')} {new Date(consulta.data.fecha).toLocaleTimeString(localeActual())}
          </span>
        )}
      </div>
      {consulta.isPending && (
        <p className="mt-6 text-apagado">{tr('Consultando el tablero…', 'Loading the dashboard…')}</p>
      )}
      {consulta.isError && (
        <div className="mt-6 rounded-lg border border-alerta/30 bg-red-50 p-4 text-sm">
          <div className="font-medium text-alerta">
            {tr('No se pudo consultar el tablero', 'Could not load the dashboard')}
          </div>
          <div className="mt-1">{consulta.error.message}</div>
        </div>
      )}
      {consulta.data && <Tablero resumen={consulta.data} />}
    </section>
  );
}

function Tablero({resumen}: {resumen: ResumenDeInventario}) {
  const bodega = resumen.enBodega;
  const total = resumen.bigBags.total;
  return (
    <div className="mt-6 space-y-6">
      {/* Orden de Daniel (7/10/2026): pendientes compactos, el comparativo con
          sus graficas primero y la ultima carga de cada archivo al final. */}
      <PendientesHoy />
      <ComparativoAnual />

      {total === 0 && (
        <div className="rounded-lg border border-dorado/40 bg-dorado-claro p-4 text-sm">
          {tr(
            'Todavía no hay Big Bags en la base de datos. La migración inicial los carga desde los Excel vigentes.',
            'There are no Big Bags in the database yet. The initial migration loads them from the current Excel files.',
          )}
        </div>
      )}

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-apagado">
          {tr('En bodega hoy', 'In the warehouse today')}
        </h2>
        <div className="mt-2 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Indicador
            titulo="Big Bags"
            valor={numero(bodega.bigBags)}
            detalle={tr(
              `${numero(resumen.bigBags.disponibles)} disponibles · ${numero(resumen.bigBags.asignados)} asignados`,
              `${numero(resumen.bigBags.disponibles)} available · ${numero(resumen.bigBags.asignados)} assigned`,
            )}
            enlace="/inventario?estado=available"
            ayuda={A.bigBagsEnBodega}
          />
          <Indicador
            titulo={tr('Toneladas secas', 'Dry tons')}
            valor={numero(bodega.tonSecas, 2)}
            detalle={tr(`${numero(bodega.tonHumedas, 2)} húmedas`, `${numero(bodega.tonHumedas, 2)} wet`)}
            ayuda={A.tonSecasEnBodega}
          />
          <Indicador
            titulo={tr('Oro', 'Gold')}
            valor={`${numero(bodega.auOz, 2)} oz`}
            detalle={`${numero(bodega.auG, 0)} g`}
            ayuda={A.oroEnBodega}
          />
          <Indicador
            titulo={tr('Plata', 'Silver')}
            valor={`${numero(bodega.agOz, 2)} oz`}
            detalle={`${numero(bodega.agG, 0)} g`}
            ayuda={A.plataEnBodega}
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-linea bg-white p-5 lg:col-span-2">
          <div className="flex items-baseline justify-between">
            <h2 className="font-medium">{tr('Por exportador', 'By exporter')}</h2>
            <span className="text-xs text-apagado">
              {tr(`${numero(total)} Big Bags en total`, `${numero(total)} Big Bags in total`)}
            </span>
          </div>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-apagado">
                <th className="py-1 font-semibold">
                  {tr('Exportador', 'Exporter')}
                  <NotaDeAyuda texto={A.exportador} />
                </th>
                <th className="py-1 text-right font-semibold">
                  Big Bags
                  <NotaDeAyuda texto={A.bigBagsTotal} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('En bodega', 'In warehouse')}
                  <NotaDeAyuda texto={A.enBodega} />
                </th>
                <th className="py-1 text-right font-semibold">
                  {tr('Ton. secas', 'Dry tons')}
                  <NotaDeAyuda texto={A.tonSecas} />
                </th>
                <th className="py-1 text-right font-semibold">
                  Au (oz)
                  <NotaDeAyuda texto={A.auOz} />
                </th>
                <th className="py-1 text-right font-semibold">
                  Ag (oz)
                  <NotaDeAyuda texto={A.agOz} />
                </th>
              </tr>
            </thead>
            <tbody>
              {resumen.porExportador.map(fila => (
                <tr key={fila.exportador} className="border-t border-linea/70">
                  <td className="py-1.5">
                    <Link
                      to={`/inventario?exportador=${encodeURIComponent(fila.exportador)}`}
                      className="text-verde hover:underline"
                    >
                      {fila.exportador}
                    </Link>
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{numero(fila.bigBags)}</td>
                  <td className="py-1.5 text-right tabular-nums">{numero(fila.enBodega)}</td>
                  <td className="py-1.5 text-right tabular-nums">{numero(fila.tonSecas, 2)}</td>
                  <td className="py-1.5 text-right tabular-nums">{numero(fila.auOz, 2)}</td>
                  <td className="py-1.5 text-right tabular-nums">{numero(fila.agOz, 2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-linea bg-white p-5">
          <div className="flex items-baseline justify-between">
            <h2 className="font-medium">
              {tr('Exportaciones', 'Exports')}
              <NotaDeAyuda texto={A.exportaciones} />
            </h2>
            <Link to="/seguimiento" className="text-xs text-verde hover:underline">
              {tr('Ver seguimiento', 'View tracking')}
            </Link>
          </div>
          <div className="mt-2 text-3xl font-semibold text-verde-oscuro">{numero(resumen.exportaciones.total)}</div>
          <div className="text-xs text-apagado">
            {tr(
              `${numero(resumen.exportaciones.propias)} propias · ${numero(resumen.exportaciones.deTerceros)} de terceros · ${numero(resumen.exportaciones.mineros)} de Mineros`,
              `${numero(resumen.exportaciones.propias)} own · ${numero(resumen.exportaciones.deTerceros)} third-party · ${numero(resumen.exportaciones.mineros)} Mineros`,
            )}
          </div>
          <ul className="mt-3 space-y-1 text-sm">
            {resumen.exportaciones.porEstado.map(e => (
              <li key={e.estado} className="flex justify-between">
                <span className="text-apagado">{e.estado}</span>
                <span className="tabular-nums">{numero(e.cantidad)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="text-sm text-apagado">
        {tr(
          `Histórico: ${numero(resumen.historico.bigBags)} Big Bags, ${numero(resumen.historico.tonSecas, 2)} toneladas secas, ${numero(resumen.historico.auOz, 2)} oz de oro y ${numero(resumen.historico.agOz, 2)} oz de plata.`,
          `All-time: ${numero(resumen.historico.bigBags)} Big Bags, ${numero(resumen.historico.tonSecas, 2)} dry tons, ${numero(resumen.historico.auOz, 2)} oz of gold and ${numero(resumen.historico.agOz, 2)} oz of silver.`,
        )}
        <NotaDeAyuda texto={A.historico} />{' '}
        <Link to="/estado" className="text-verde hover:underline">
          {tr('Ver estado del sistema', 'View system status')}
        </Link>
      </div>

      <FrescuraDeDatos />
    </div>
  );
}

function Indicador({
  titulo,
  valor,
  detalle,
  enlace,
  ayuda,
}: {
  titulo: string;
  valor: string;
  detalle?: string;
  enlace?: string;
  ayuda?: Texto;
}) {
  const contenido = (
    <>
      <div className="pr-5 text-xs font-semibold uppercase tracking-wide text-apagado">{titulo}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-verde-oscuro">{valor}</div>
      {detalle && <div className="mt-1 text-xs text-apagado">{detalle}</div>}
    </>
  );
  const clase = 'block h-full rounded-lg border border-linea bg-white p-5';
  // La nota va fuera del enlace (un boton no puede ir dentro de un enlace).
  return (
    <div className="relative">
      {enlace ? (
        <Link to={enlace} className={`${clase} hover:border-verde`}>
          {contenido}
        </Link>
      ) : (
        <div className={clase}>{contenido}</div>
      )}
      {ayuda && (
        <span className="absolute top-4 right-4">
          <NotaDeAyuda titulo={titulo} texto={ayuda} />
        </span>
      )}
    </div>
  );
}
